import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Filter,
  MapPin,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  User,
  Trash2
} from 'lucide-react';
import { Complaint } from '../types';
import apiService from '../services/api';
import { getTimeRemaining, formatDateTime, getStatusColor, getStatusText } from '../utils/timeUtils';

const ITEMS_PER_PAGE = 12;

const ComplaintsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [totalComplaints, setTotalComplaints] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const currentUser = apiService.getCurrentUser();
  const isAdmin = currentUser?.userType === 'admin';

  const handleDelete = async (e: React.MouseEvent, complaintId: string) => {
    e.stopPropagation();
    if (!window.confirm(isRTL ? 'هل أنت متأكد من حذف هذه الشكوى؟' : 'Are you sure you want to delete this complaint?')) return;
    try {
      setDeletingId(complaintId);
      await apiService.deleteComplaint(complaintId);
      // Refresh current page after delete
      fetchComplaints(currentPage);
    } catch (err) {
      setError(isRTL ? 'فشل حذف الشكوى' : 'Failed to delete complaint');
    } finally {
      setDeletingId(null);
    }
  };

  const fetchComplaints = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const data = await apiService.getComplaints(filterStatus || undefined, page, ITEMS_PER_PAGE);
      setComplaints(data.results);
      setTotalComplaints(data.total);
      setTotalPages(data.totalPages);
      setError('');
    } catch (err: any) {
      setError(t('complaints.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, t]);

  useEffect(() => {
    fetchComplaints(currentPage);
    const interval = setInterval(() => fetchComplaints(currentPage), 60000);
    return () => clearInterval(interval);
  }, [fetchComplaints, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setCurrentPage(1);
      fetchComplaints(1);
      return;
    }
    try {
      setLoading(true);
      const data = await apiService.searchComplaints(searchQuery);
      setComplaints(data);
      setTotalComplaints(data.length);
      setTotalPages(1);
    } catch (err) {
      setError(t('complaints.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSelect = (status: string) => {
    setFilterStatus(status === 'all' ? null : status);
    setShowFilterMenu(false);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!e.target.value.trim()) {
      setCurrentPage(1);
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-orange-100 text-orange-800 border-orange-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      rework_requested: 'bg-red-100 text-red-800 border-red-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200',
      closed_by_admin: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return statusColors[status] || statusColors.pending;
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <CheckCircle className="w-4 h-4" />;
      case 'closed_by_admin': return <XCircle className="w-4 h-4" />;
      case 'rework_requested': return <XCircle className="w-4 h-4" />;
      case 'in_progress': return <Loader className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-800/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-black text-white mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('complaints.assignedTitle')}
                </h1>
                <p className="text-brand-50 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'إدارة ومتابعة البلاغات' : 'Manage and track all complaints'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => fetchComplaints(currentPage)}
                  disabled={loading}
                  className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Search and Filter Bar */}
        <div className="relative z-40 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8 backdrop-blur-sm">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('complaints.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium`}
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 hover:shadow-xl"
              >
                <Filter className="w-5 h-5" />
                <span>{t('common.filter')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showFilterMenu ? 'rotate-180' : ''}`} />
              </button>

              {showFilterMenu && (
                <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 min-w-[220px] z-50 animate-fadeIn">
                  <button onClick={() => handleFilterSelect('all')} className="w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors font-medium text-gray-700 hover:text-brand-700">
                    {t('complaints.statusOptions.all')}
                  </button>
                  <button onClick={() => handleFilterSelect('pending')} className="w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors font-medium text-gray-700 hover:text-brand-700">
                    {t('complaints.statusOptions.pending')}
                  </button>
                  <button onClick={() => handleFilterSelect('in_progress')} className="w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors font-medium text-gray-700 hover:text-brand-700">
                    {t('complaints.statusOptions.in_progress')}
                  </button>
                  <button onClick={() => handleFilterSelect('rework_requested')} className="w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors font-medium text-gray-700 hover:text-brand-700">
                    {t('complaints.statusOptions.rework_requested')}
                  </button>
                  <button onClick={() => handleFilterSelect('resolved')} className="w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors font-medium text-gray-700 hover:text-brand-700">
                    {t('complaints.statusOptions.resolved')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : (
          <>
            {/* Complaints Grid */}
            {complaints.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">{t('complaints.noComplaints')}</h3>
                <p className="text-gray-500">{isRTL ? 'لا توجد بلاغات حالياً' : 'No complaints found'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {complaints.map((complaint) => {
                  const timeInfo = getTimeRemaining(complaint.slaDeadline);
                  return (
                    <div
                      key={complaint.id}
                      className="group relative bg-white rounded-2xl shadow-lg border-l-4 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
                      style={{ borderLeftColor: getStatusColor(complaint.status) }}
                      onClick={() => navigate(`/complaints/${complaint.id}`)}
                    >
                      {/* Card Content */}
                      <div className="p-6">
                        {/* Status and Rework Badges */}
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border ${getStatusBadgeStyle(complaint.status)}`}>
                            {getStatusIcon(complaint.status)}
                            {getStatusText(complaint.status)}
                          </span>
                          {complaint.reworkCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                              {`${t('complaints.rework')}: ${complaint.reworkCount}`}
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        <h3 className="text-lg font-bold text-gray-800 mb-4 line-clamp-2 group-hover:text-brand-700 transition-colors" dir={isRTL ? 'rtl' : 'ltr'}>
                          {complaint.description}
                        </h3>

                        {/* Location */}
                        <div className="flex items-center gap-2 mb-3 text-gray-600">
                          <MapPin className="w-4 h-4 text-brand-600 flex-shrink-0" />
                          <span className="text-sm font-medium truncate" dir={isRTL ? 'rtl' : 'ltr'}>{complaint.locationName}</span>
                        </div>

                        {/* Evaluator Name */}
                        {(complaint.evaluator as any)?.name && (
                          <div className="flex items-center gap-2 mb-3 text-gray-600">
                            <User className="w-4 h-4 text-brand-600 flex-shrink-0" />
                            <span className="text-sm font-medium truncate" dir={isRTL ? 'rtl' : 'ltr'}>
                              {isRTL ? 'أنشأ بواسطة: ' : 'Created by: '}
                              <span className="font-semibold text-gray-800">{(complaint.evaluator as any).name}</span>
                            </span>
                          </div>
                        )}

                        {/* Created At */}
                        <div className="flex items-center gap-2 mb-4 text-gray-600">
                          <Clock className="w-4 h-4 text-brand-600 flex-shrink-0" />
                          <span className="text-sm font-medium">{formatDateTime(complaint.createdAt)}</span>
                        </div>

                        {/* SLA Progress */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-bold ${timeInfo.isOverdue ? 'text-red-600' : 'text-green-600'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                              {timeInfo.text}
                            </span>
                            <span className="text-xs font-semibold text-gray-500">
                              {timeInfo.percentage}%
                            </span>
                          </div>
                          <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                timeInfo.isOverdue
                                  ? 'bg-gradient-to-r from-red-500 to-red-600'
                                  : timeInfo.percentage < 30
                                  ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                                  : 'bg-gradient-to-r from-green-500 to-green-600'
                              }`}
                              style={{ width: `${Math.min(timeInfo.percentage, 100)}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Rework Notes */}
                        {complaint.reworkNotes && (
                          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-3 mb-4">
                            <p className="text-xs text-amber-800 font-medium line-clamp-2" dir={isRTL ? 'rtl' : 'ltr'}>
                              {complaint.reworkNotes}
                            </p>
                          </div>
                        )}

                        {/* Buttons Row */}
                        <div className="flex gap-2">
                          <button className="flex-1 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-xl transition-all duration-300 group-hover:shadow-lg">
                            {t('common.viewDetails')}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={(e) => handleDelete(e, complaint.id)}
                              disabled={deletingId === complaint.id}
                              className="p-3 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 border border-red-200 rounded-xl transition-all duration-300 disabled:opacity-50"
                              title={isRTL ? 'حذف الشكوى' : 'Delete complaint'}
                            >
                              {deletingId === complaint.id
                                ? <Loader className="w-5 h-5 animate-spin" />
                                : <Trash2 className="w-5 h-5" />
                              }
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Bottom Accent */}
                      <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-600 to-emerald-600"></div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
                <p className="text-sm font-medium text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL
                    ? `عرض ${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, totalComplaints)} من ${totalComplaints} شكوى`
                    : `Showing ${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, totalComplaints)} of ${totalComplaints} complaints`
                  }
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl border border-gray-200 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isRTL ? <ChevronRight className="w-5 h-5 text-gray-600" /> : <ChevronLeft className="w-5 h-5 text-gray-600" />}
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === 'ellipsis' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 font-medium">…</span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => handlePageChange(item as number)}
                          className={`w-10 h-10 rounded-xl text-sm font-bold transition-all duration-200 ${
                            currentPage === item
                              ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg'
                              : 'border border-gray-200 text-gray-600 hover:bg-brand-50 hover:border-brand-300'
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )}

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl border border-gray-200 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isRTL ? <ChevronLeft className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default ComplaintsPage;
